<script setup lang="ts">
// The population view's nine bivariate classes as rows above the Zones ledger:
// the same 3×3 grid the Analysis pane's legend filters by, printed where the
// ledger it narrows is read. The rows are derived, not zones — no database id,
// nothing persisted, and nothing enters `spec.zones`. A class click materialises
// its dissolved geometry as the view's `Population: <class name>` temporary
// zone, which is what narrows the ledger; clicking the active class clears it.
//
// Counts come from the payload's cells, so while a class filters the spec the
// view re-reads under that class's geometry and the rows describe the cells that
// re-read kept. A class with no cells in scope is inert: it has no geometry to
// materialise, and the store's own toggle refuses it.
import { computed } from "vue";

import { formatBigNumber } from "@/format";
import type { MapReviewPopulationView } from "@/type/api";

import { bivariateFill, classLabel } from "./palette";

const props = defineProps<{
	/** The population payload; null until the first read lands. */
	view: MapReviewPopulationView | null;
	/** The class currently filtering the spec, or null for none. */
	selectedClass: number | null;
}>();

const emit = defineEmits<{
	"select-class": [index: number];
}>();

interface ClassRow {
	index: number;
	label: string;
	count: number;
	fill: string;
}

/** Nine rows in class-index order — falling/low through rising/high. A cell
 * with no level or slope carries a null class and belongs to no row. */
const rows = computed<ClassRow[]>(() => {
	const view = props.view;
	if (!view) return [];
	const counts = new Array<number>(9).fill(0);
	for (const cell of view.cells) {
		if (cell.class != null && cell.class >= 0 && cell.class <= 8) {
			counts[cell.class] += 1;
		}
	}
	return Array.from({ length: 9 }, (_, index) => ({
		index,
		label: classLabel(index),
		count: counts[index],
		fill: bivariateFill(index),
	}));
});
</script>

<template>
	<section
		v-if="rows.length > 0"
		class="population-zone-rows"
		aria-labelledby="population-class-rows-title"
	>
		<header class="pzr-head">
			<h3 id="population-class-rows-title" class="pzr-title">
				Population classes — current filter
			</h3>
			<p class="pzr-note">
				Derived from the H3 population view, not saved zones: each count is the
				cells in that class in the current scope. A class narrows this ledger to
				its cells; click it again to clear.
			</p>
		</header>

		<ul class="pzr-list">
			<li v-for="row in rows" :key="row.index" class="pzr-item">
				<button
					type="button"
					class="pzr-row"
					:class="{ 'pzr-row--selected': selectedClass === row.index }"
					:disabled="row.count === 0 && selectedClass !== row.index"
					:aria-pressed="selectedClass === row.index"
					:title="
						row.count === 0 && selectedClass !== row.index
							? `${row.label} — no cells in scope`
							: `${row.label} — ${formatBigNumber(row.count)} cells`
					"
					@click="emit('select-class', row.index)"
				>
					<span
						class="pzr-swatch"
						:style="{ background: row.fill }"
						aria-hidden="true"
					></span>
					<span class="pzr-label">{{ row.label }}</span>
					<span class="pzr-count">{{ formatBigNumber(row.count) }}</span>
					<span class="pzr-select">
						{{ selectedClass === row.index ? "Clear" : "Select" }}
					</span>
				</button>
			</li>
		</ul>
	</section>
</template>

<style scoped>
/* The section reads as the ledger's own: the same 0.75rem type, the same 28px
   row height and rule as `.dt-row`, so the derived rows and the zone rows below
   them share one rhythm. The nine classes sit as the 3×3 grid the population
   view is built on — slope is the line, level the place along it — so all nine
   are visible in the short default drawer instead of hidden behind a scroll.
   The list still caps at half the drawer, which only bites when the three
   columns fall back to one on a narrow drawer; there the list scrolls itself. */
.population-zone-rows {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	max-height: 50%;
	overflow: hidden;
	background: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	font-size: 0.75rem;
}

.pzr-head {
	flex: 0 0 auto;
	padding: 4px 8px 3px;
}

.pzr-title {
	margin: 0;
	color: #343a40;
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1.35;
}

.pzr-note {
	margin: 1px 0 0;
	color: #6c757d;
	font-size: 0.6875rem;
	line-height: 1.3;
}

/* One 1px rule between cells, drawn by the gap rather than by each row, so the
   nine cells read as one small table. */
.pzr-list {
	flex: 1 1 auto;
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 1px;
	min-height: 0;
	margin: 0;
	padding: 0;
	overflow-y: auto;
	background: #eef0f2;
	list-style: none;
}

/* A narrow drawer cannot hold three cells, so the grid becomes the vertical
   list of rows it was before and scrolls within the cap. */
@media (max-width: 720px) {
	.pzr-list {
		grid-template-columns: minmax(0, 1fr);
	}
}

.pzr-item {
	margin: 0;
	min-width: 0;
}

/* The whole cell is the control, so the target is the whole row; the native
   button reset keeps it from announcing itself as chrome. The gap between
   cells supplies the rule, exactly as a ledger row supplies its own. */
.pzr-row {
	display: grid;
	grid-template-columns: 10px minmax(0, 1fr) auto auto;
	align-items: center;
	gap: 8px;
	width: 100%;
	height: 28px;
	padding: 0 8px;
	border: 0;
	background: #fff;
	color: #212529;
	font: inherit;
	text-align: left;
	cursor: pointer;
}

.pzr-row:hover {
	background: #f8f9fa;
}

.pzr-row:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: -2px;
}

.pzr-row--selected,
.pzr-row--selected:hover {
	background: #e7f1ff;
}

/* An empty class has nothing to materialise, so the row is inert rather than
   pretending the click will narrow anything. */
.pzr-row:disabled {
	background: #fff;
	color: #adb5bd;
	cursor: default;
}

.pzr-swatch {
	width: 10px;
	height: 10px;
	border-radius: 2px;
	box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.pzr-row:disabled .pzr-swatch {
	opacity: 0.4;
}

.pzr-label {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.pzr-count {
	color: #495057;
	font-variant-numeric: tabular-nums;
	text-align: right;
}

.pzr-row--selected .pzr-count {
	color: #1864ab;
}

.pzr-row:disabled .pzr-count {
	color: #adb5bd;
}

/* The select control's affordance. It reads as a button, but the row around it
   is the actual target — the same gesture either way. */
.pzr-select {
	padding: 0 5px;
	border: 1px solid #ced4da;
	border-radius: 2px;
	background: #fff;
	color: #495057;
	font-size: 0.6875rem;
	line-height: 16px;
}

.pzr-row:hover .pzr-select {
	border-color: #0d6efd;
	color: #0d6efd;
}

.pzr-row--selected .pzr-select,
.pzr-row--selected:hover .pzr-select {
	border-color: #0d6efd;
	color: #0d6efd;
}

.pzr-row:disabled .pzr-select {
	border-color: #e9ecef;
	background: #f8f9fa;
	color: #adb5bd;
}
</style>
